// Caja: apertura de turno, movimientos de caja y cierre con arqueo.
// POS → Venta / Caja → Apertura / Movimientos / Cierre.
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, DoorClosed, DoorOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Imprimible, PrintButton } from '@/components/print/DocPrint';
import { Denominacion, denominacionesActivas, fieldCls, money, pos, posRpc, useTerminalCaja } from './shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

interface Sesion { id: string; codigo: string; fecha_apertura: string }
interface Resumen {
  codigo: string; fondo_inicial: number; n_ventas: number; total_ventas: number;
  efectivo_ventas: number; ingresos: number; egresos: number; efectivo_sistema: number;
  formas: { nombre: string; tipo: string; total: number }[];
  movimientos: { tipo: string; monto: number; concepto: string | null; created_at: string }[];
}

// Gate común: resuelve caja del equipo y (opcional) sesión abierta
function useCajaSesion() {
  const { caja, loading } = useTerminalCaja();
  const sesionQ = useQuery({ queryKey: ['pos-sesion', caja?.id], enabled: !!caja, queryFn: () => posRpc<Sesion | null>('pos_sesion_activa', { p_caja_id: caja!.id }), staleTime: 0, refetchOnMount: 'always' });
  return { caja, loading, sesion: sesionQ.data ?? null, sesionLoading: sesionQ.isFetching, refetchSesion: sesionQ.refetch };
}

function SinTerminal() {
  return <div className="p-6"><div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">Este equipo no está registrado a una caja. Abre <span className="font-medium">POS → Punto de venta</span> para registrarlo.</div></div>;
}

// ---------------------------------------------------------------------------
// Apertura
// ---------------------------------------------------------------------------
export function AperturaCaja() {
  const qc = useQueryClient();
  const { caja, loading, sesion, refetchSesion } = useCajaSesion();
  const [fondo, setFondo] = useState('0');
  const abrir = useMutation({
    mutationFn: () => posRpc('pos_abrir_sesion', { p_caja_id: caja!.id, p_fondo: Number(fondo) || 0 }),
    onSuccess: () => { toast.success('Sesión abierta'); void qc.invalidateQueries({ queryKey: ['pos-sesion', caja!.id] }); refetchSesion(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  if (loading) return <Cargando />;
  if (!caja) return <SinTerminal />;
  return (
    <div className="p-6 max-w-md mx-auto space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><DoorOpen className="w-5 h-5" /> Apertura de caja · {caja.nombre}</h1>
      {sesion ? (
        <div className="rounded-md border bg-muted/40 px-4 py-3 text-sm">Ya hay un turno abierto: <span className="font-medium">{sesion.codigo}</span> (desde {new Date(sesion.fecha_apertura).toLocaleString(LOCALE)}).</div>
      ) : (
        <>
          <label className="block space-y-1"><span className="text-xs font-medium text-muted-foreground">Fondo inicial</span>
            <input type="number" step="0.01" className={fieldCls} value={fondo} onChange={(e) => setFondo(e.target.value)} /></label>
          <Button isLoading={abrir.isPending} onClick={() => abrir.mutate()}>Abrir sesión</Button>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Movimientos de caja
// ---------------------------------------------------------------------------
export function MovimientosCaja() {
  const qc = useQueryClient();
  const { caja, loading, sesion } = useCajaSesion();
  const [tipo, setTipo] = useState('ingreso');
  const [monto, setMonto] = useState('');
  const [concepto, setConcepto] = useState('');
  const { data: movs = [], isFetching } = useQuery<{ id: string; tipo: string; monto: number; concepto: string | null; created_at: string }[]>({
    queryKey: ['pos-movs', sesion?.id], enabled: !!sesion,
    queryFn: () => pos.list('movimientos_caja', `?select=*&sesion_id=eq.${sesion!.id}&order=created_at.desc`),
  });
  const registrar = useMutation({
    mutationFn: () => posRpc('pos_movimiento_caja', { payload: { sesion_id: sesion!.id, tipo, monto: Number(monto) || 0, concepto } }),
    onSuccess: () => { toast.success('Movimiento registrado'); setMonto(''); setConcepto(''); void qc.invalidateQueries({ queryKey: ['pos-movs', sesion!.id] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  if (loading) return <Cargando />;
  if (!caja) return <SinTerminal />;
  if (!sesion) return <SinTurno />;
  return (
    <div className="p-6 space-y-4 max-w-2xl">
      <h1 className="text-xl font-semibold flex items-center gap-2"><ArrowRightLeft className="w-5 h-5" /> Movimientos de caja · {sesion.codigo}</h1>
      <Card><CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 items-end">
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Tipo</span>
          <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="ingreso">Ingreso</option><option value="egreso">Egreso</option><option value="retiro">Retiro</option>
          </select></label>
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Monto</span>
          <input type="number" step="0.01" className={fieldCls} value={monto} onChange={(e) => setMonto(e.target.value)} /></label>
        <label className="block space-y-1 md:col-span-2"><span className="text-xs text-muted-foreground">Concepto</span>
          <input className={fieldCls} value={concepto} onChange={(e) => setConcepto(e.target.value)} /></label>
        <Button className="md:col-span-4" isLoading={registrar.isPending} disabled={!monto || Number(monto) <= 0} onClick={() => registrar.mutate()}>Registrar</Button>
      </CardContent></Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Movimientos del turno ({movs.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Hora</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Concepto</th><th className="py-2 pr-3 text-right">Monto</th></tr></thead>
            <tbody>
              {movs.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Sin movimientos</td></tr>}
              {movs.map((m) => (
                <tr key={m.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 whitespace-nowrap">{new Date(m.created_at).toLocaleTimeString(LOCALE)}</td>
                  <td className="py-2 pr-3 capitalize">{m.tipo}</td><td className="py-2 pr-3 text-muted-foreground">{m.concepto ?? '—'}</td>
                  <td className={`py-2 pr-3 text-right font-medium ${m.tipo === 'ingreso' ? 'text-emerald-600' : 'text-red-600'}`}>{m.tipo === 'ingreso' ? '' : '-'}{money(m.monto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cierre / Arqueo
// ---------------------------------------------------------------------------
export function CierreArqueo() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { caja, loading, sesion, refetchSesion } = useCajaSesion();
  const { data: denoms = [] } = useQuery<Denominacion[]>({ queryKey: ['pos-denoms'], queryFn: denominacionesActivas });
  const { data: resumen } = useQuery<Resumen>({ queryKey: ['pos-resumen', sesion?.id], enabled: !!sesion, queryFn: () => posRpc('pos_sesion_resumen', { p_sesion_id: sesion!.id }), staleTime: 0, refetchOnMount: 'always' });
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [obs, setObs] = useState('');
  const [cerrado, setCerrado] = useState<{ efectivo_sistema: number; efectivo_contado: number; diferencia: number } | null>(null);

  const contado = useMemo(() => denoms.reduce((s, d) => s + Number(d.valor) * (counts[d.id] || 0), 0), [denoms, counts]);
  const sistema = resumen?.efectivo_sistema ?? 0;
  const diferencia = Math.round((contado - sistema) * 100) / 100;

  const cerrar = useMutation({
    mutationFn: () => {
      const detalle = denoms.filter((d) => counts[d.id]).map((d) => ({ valor: d.valor, tipo: d.tipo, cantidad: counts[d.id], subtotal: Number(d.valor) * counts[d.id] }));
      return posRpc<{ efectivo_sistema: number; efectivo_contado: number; diferencia: number }>('pos_cerrar_sesion', { payload: { sesion_id: sesion!.id, efectivo_contado: contado, detalle, observaciones: obs || null } });
    },
    onSuccess: (r) => { toast.success('Caja cerrada'); setCerrado(r); void qc.invalidateQueries({ queryKey: ['pos-sesion', caja!.id] }); refetchSesion(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (loading) return <Cargando />;
  if (!caja) return <SinTerminal />;
  if (!sesion && !cerrado) return <SinTurno />;

  if (cerrado) {
    return (
      <div className="p-6 max-w-md mx-auto">
        <div className="no-print mb-3 flex items-center justify-between"><h1 className="text-xl font-semibold">Caja cerrada</h1><PrintButton /></div>
        <Imprimible titulo="Cierre de caja (Z)" subtitulo={resumen?.codigo}>
          <div className="space-y-1 text-sm">
            <Fila k="Caja" v={caja.nombre} /><Fila k="Ventas" v={`${resumen?.n_ventas ?? 0} · ${money(resumen?.total_ventas ?? 0)}`} />
            <Fila k="Efectivo del sistema" v={money(cerrado.efectivo_sistema)} />
            <Fila k="Efectivo contado" v={money(cerrado.efectivo_contado)} />
            <div className={`flex justify-between font-bold ${cerrado.diferencia === 0 ? 'text-emerald-600' : 'text-red-600'}`}><span>Diferencia</span><span>{money(cerrado.diferencia)}</span></div>
          </div>
        </Imprimible>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><DoorClosed className="w-5 h-5" /> Cierre / Arqueo · {sesion!.codigo}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Resumen del turno */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Resumen del turno</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Fila k="Fondo inicial" v={money(resumen?.fondo_inicial ?? 0)} />
            <Fila k="Ventas" v={`${resumen?.n_ventas ?? 0} · ${money(resumen?.total_ventas ?? 0)}`} />
            <div className="border-t pt-2 text-xs font-semibold uppercase text-muted-foreground">Por forma de pago</div>
            {(resumen?.formas ?? []).map((f, i) => <Fila key={i} k={f.nombre} v={money(f.total)} />)}
            <div className="border-t pt-2"><Fila k="Efectivo ventas" v={money(resumen?.efectivo_ventas ?? 0)} />
              <Fila k="Ingresos" v={money(resumen?.ingresos ?? 0)} /><Fila k="Egresos/retiros" v={money(resumen?.egresos ?? 0)} /></div>
            <div className="flex justify-between border-t pt-2 font-semibold"><span>Efectivo esperado</span><span>{money(sistema)}</span></div>
          </CardContent>
        </Card>

        {/* Arqueo por denominación */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Arqueo de efectivo</CardTitle></CardHeader>
          <CardContent>
            {denoms.length === 0 && <p className="text-sm text-muted-foreground">No hay denominaciones configuradas (POS → Configuración → Denominaciones). Puedes igual cerrar con el contado en 0.</p>}
            <table className="w-full text-sm">
              <tbody>
                {denoms.map((d) => (
                  <tr key={d.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2">{money(d.valor)} <span className="text-xs text-muted-foreground capitalize">{d.tipo}</span></td>
                    <td className="py-1.5 pr-2 w-24"><input type="number" min="0" className={`${fieldCls} h-8 text-right`} value={counts[d.id] || ''} onChange={(e) => setCounts((c) => ({ ...c, [d.id]: Math.max(0, parseInt(e.target.value) || 0) }))} /></td>
                    <td className="py-1.5 text-right">{money(Number(d.valor) * (counts[d.id] || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 space-y-1 border-t pt-2 text-sm">
              <div className="flex justify-between font-semibold"><span>Efectivo contado</span><span>{money(contado)}</span></div>
              <div className={`flex justify-between font-bold ${diferencia === 0 ? 'text-emerald-600' : 'text-red-600'}`}><span>Diferencia</span><span>{money(diferencia)}</span></div>
            </div>
            <label className="block space-y-1 mt-3"><span className="text-xs text-muted-foreground">Observaciones</span>
              <input className={fieldCls} value={obs} onChange={(e) => setObs(e.target.value)} /></label>
            <Button className="w-full mt-3" isLoading={cerrar.isPending}
              onClick={async () => { if (await dlg.confirmar('¿Cerrar la caja?', { detalle: `Diferencia: ${money(diferencia)}.` })) cerrar.mutate(); }}>Cerrar caja</Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Cargando() { return <div className="flex items-center justify-center p-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>; }
function SinTurno() { return <div className="p-6"><div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">No hay un turno abierto en esta caja. Ábrelo en <span className="font-medium">POS → Apertura de caja</span>.</div></div>; }
function Fila({ k, v }: { k: string; v: string }) { return <div className="flex justify-between"><span className="text-muted-foreground">{k}</span><span>{v}</span></div>; }
